import { taskAxiosInstance } from "../axiosInstance";
import type { JsonValue, TaskAction, TaskStatus } from "./types";
import type { TaskLink } from "./taskLinkApi";
import type { TaskOccurrence } from "./taskOccurrenceApi";

export interface Task {
    id: number;
    globalId: string;
    sourceApp: string | null;
    action: TaskAction | null;
    rrule: string | null;
    title: string | null;
    description: string | null;
    ownerUserId: number | null;
    status: TaskStatus;
    annualCycle: boolean;
    payload: JsonValue | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    createdById: number;
    updatedById: number;
    groupId: number | null;
    listId: number | null;
    sortOrder: number;
    duration: number | null;
    instanceId: number;
    links?: TaskLink[];
    ownerUser?: { id: number; firstName: string; lastName: string } | null;
    occurrences?: TaskOccurrence[];
}

export interface TaskComment {
    id: number;
    taskId: number;
    comment: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    createdById: number;
}

export interface TaskCommentQuery {
    skip?: number;
    take?: number;
}

export interface TaskCommentListResponse {
    data: TaskComment[];
    total: number;
}

export interface CreateTaskCommentInput {
    comment: string;
}

export interface UpdateTaskCommentInput {
    comment: string;
}

export interface TaskQuery {
    instanceId?: number;
    skip?: number;
    take?: number;
    status?: TaskStatus;
    ownerUserId?: number;
    sourceApp?: string;
    action?: TaskAction;
    search?: string;
    includeDeleted?: boolean;
    listId?: number;
    annualCycle?: boolean;
}

export interface TaskListResponse {
    data: Task[];
    total: number;
}

export interface CreateTaskInput {
    title: string;
    instanceId: number;
    description?: string | null;
    sourceApp?: string;
    action?: TaskAction;
    annualCycle?: boolean;
    ownerUserId?: number;
    status?: TaskStatus;
    rrule?: string | null;
    payload?: JsonValue | null;
    listId?: number | null;
    groupId?: number | null;
    sortOrder?: number;
    duration?: number;
}

export type UpdateTaskInput = Partial<
    Pick<
        CreateTaskInput,
        | "title"
        | "description"
        | "ownerUserId"
        | "status"
        | "rrule"
        | "payload"
        | "groupId"
        | "sortOrder"
        | "duration"
    >
>;

export const getTasks = async (
    query: TaskQuery = {},
    { signal }: { signal?: AbortSignal } = {},
): Promise<TaskListResponse> => {
    const params = new URLSearchParams();
    if (query.instanceId !== undefined) params.set("instanceId", query.instanceId.toString());
    if (query.skip !== undefined) params.set("skip", query.skip.toString());
    if (query.take !== undefined) params.set("take", query.take.toString());
    if (query.status) params.set("status", query.status);
    if (query.ownerUserId !== undefined) params.set("ownerUserId", query.ownerUserId.toString());
    if (query.sourceApp) params.set("sourceApp", query.sourceApp);
    if (query.action) params.set("action", query.action);
    if (query.search) params.set("search", query.search);
    if (query.includeDeleted !== undefined)
        params.set("includeDeleted", query.includeDeleted ? "true" : "false");
    if (query.listId !== undefined) params.set("listId", query.listId.toString());
    if (query.annualCycle !== undefined)
        params.set("annualCycle", query.annualCycle ? "true" : "false");

    const queryString = params.toString();

    return (
        await taskAxiosInstance.get<TaskListResponse>(
            `/tasks${queryString ? `?${queryString}` : ""}`,
            { signal },
        )
    ).data;
};

export const getTaskById = async (id: number): Promise<Task> => {
    const response = (await taskAxiosInstance.get<Task>(`/tasks/${id}`)).data;
    return response;
};

export const createTask = async (data: CreateTaskInput): Promise<Task> => {
    const response = (await taskAxiosInstance.post<Task>("/tasks", data)).data;
    return response;
};

export const updateTask = async (id: number, data: UpdateTaskInput): Promise<Task> => {
    const response = (await taskAxiosInstance.patch<Task>(`/tasks/${id}`, data)).data;
    return response;
};

export const deleteTask = async (id: number): Promise<Task> => {
    const response = (await taskAxiosInstance.delete<Task>(`/tasks/${id}`)).data;
    return response;
};

export interface TaskInstance {
    id: number;
    productInstanceId: number;
}

export const getTaskInstance = async (productInstanceId: number): Promise<TaskInstance> => {
    const response = (
        await taskAxiosInstance.get<TaskInstance>("/task-instances", {
            params: { productInstanceId },
        })
    ).data;
    return response;
};
